import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { motion, useReducedMotion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRight,
  faClock,
  faEnvelope,
  faLocationDot,
  faMessage,
  faPhone
} from '@fortawesome/free-solid-svg-icons';
import Banner from './Banner';
import { sendWebsiteEmail } from '../api';
import '../styles/Contact.css';

const createEmptyForm = () => ({
  type: 'contact',
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  subject: '',
  message: '',
  website: ''
});

const pageCopy = {
  fr: {
    accent: 'nous',
    eyebrow: 'Une équipe à votre écoute',
    formTitle: 'Parlez-nous de votre demande.',
    formIntro: 'Quelques informations suffisent. Notre équipe vous répond avec une solution adaptée à votre déplacement.',
    detailsIntro: 'Pour une demande urgente ou une question simple, vous pouvez également nous joindre directement.',
    response: 'Réponse personnalisée',
    responseText: 'Nous revenons vers vous dans les meilleurs délais pendant nos heures d’ouverture.',
    required: 'Ce champ est requis',
    send: 'Envoyer ma demande'
  },
  nl: {
    accent: 'ons op',
    eyebrow: 'Een team dat naar u luistert',
    formTitle: 'Vertel ons over uw aanvraag.',
    formIntro: 'Enkele gegevens volstaan. Ons team antwoordt met een oplossing die bij uw verplaatsing past.',
    detailsIntro: 'Voor een dringende aanvraag of een eenvoudige vraag kunt u ons ook rechtstreeks bereiken.',
    response: 'Persoonlijk antwoord',
    responseText: 'Wij nemen tijdens onze openingsuren zo snel mogelijk contact met u op.',
    required: 'Dit veld is verplicht',
    send: 'Mijn aanvraag versturen'
  }
};

function Contact() {
  const { t, i18n } = useTranslation();
  const reducedMotion = useReducedMotion();
  const lang = i18n.resolvedLanguage?.startsWith('nl') ? 'nl' : 'fr';
  const c = pageCopy[lang];
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState(createEmptyForm);
  const [errors, setErrors] = useState({});
  const formStartedAt = useRef(Date.now());

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) newErrors.firstName = t('contact.errors.firstNameRequired');
    else if (formData.firstName.trim().length < 2) newErrors.firstName = t('contact.errors.firstNameMinLength');
    if (!formData.lastName.trim()) newErrors.lastName = t('contact.errors.lastNameRequired');

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) newErrors.email = t('contact.errors.emailRequired');
    else if (!emailRegex.test(formData.email)) newErrors.email = t('contact.errors.emailInvalid');

    const phoneRegex = /^(?:(?:\+|00)32|0)(?:\d{9}|\d{8})$/;
    if (!formData.phone.trim()) newErrors.phone = t('contact.errors.phoneRequired');
    else if (!phoneRegex.test(formData.phone.replace(/\s/g, ''))) newErrors.phone = t('contact.errors.phoneInvalid');

    if (!formData.subject.trim()) newErrors.subject = c.required;
    if (!formData.message.trim()) newErrors.message = t('contact.errors.messageRequired');
    else if (formData.message.trim().length < 10) newErrors.message = t('contact.errors.messageMinLength');

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = ({ target: { name, value } }) => {
    setFormData((current) => ({ ...current, [name]: value }));
    if (errors[name]) setErrors((current) => ({ ...current, [name]: '' }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      await sendWebsiteEmail({
        ...formData,
        formDurationMs: Date.now() - formStartedAt.current
      });
      toast.success(t('contact.success'));
      setFormData(createEmptyForm());
      formStartedAt.current = Date.now();
    } catch (error) {
      console.error('Erreur lors de l’envoi de l’email :', error);
      toast.error(error.message || t('contact.error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const fieldClass = (name) => `contact-field${errors[name] ? ' has-error' : ''}`;
  const motionProps = reducedMotion
    ? { initial: false }
    : { initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, amount: 0.2 } };

  return (
    <main className="contact-page">
      <Banner title={t('contact.title')} subtitle={t('contact.subtitle')} accent={c.accent} />

      <section className="contact-main" aria-labelledby="contact-form-title">
        <div className="contact-layout">
          <motion.div id="contact-form" className="contact-form-container" {...motionProps} transition={{ duration: 0.5, delay: 0.08 }}>
            <div className="contact-form-heading">
              <span>{t('contact.form.title')}</span>
              <h2 id="contact-form-title">{c.formTitle}</h2>
              <p>{c.formIntro}</p>
            </div>

            <form onSubmit={handleSubmit} className="contact-form" noValidate>
              <div className="sr-only" aria-hidden="true">
                <label htmlFor="contact-website">Ne pas remplir ce champ</label>
                <input id="contact-website" name="website" value={formData.website} onChange={handleChange} tabIndex="-1" autoComplete="off" />
              </div>
              <div className="contact-fields-grid">
                <div className={fieldClass('firstName')}>
                  <label htmlFor="firstName">{t('contact.firstName')}</label>
                  <input type="text" id="firstName" name="firstName" value={formData.firstName} onChange={handleChange} autoComplete="given-name" aria-invalid={Boolean(errors.firstName)} />
                  {errors.firstName && <span className="error-message">{errors.firstName}</span>}
                </div>
                <div className={fieldClass('lastName')}>
                  <label htmlFor="lastName">{t('contact.lastName')}</label>
                  <input type="text" id="lastName" name="lastName" value={formData.lastName} onChange={handleChange} autoComplete="family-name" aria-invalid={Boolean(errors.lastName)} />
                  {errors.lastName && <span className="error-message">{errors.lastName}</span>}
                </div>
                <div className={fieldClass('email')}>
                  <label htmlFor="email">{t('contact.email')}</label>
                  <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} autoComplete="email" aria-invalid={Boolean(errors.email)} />
                  {errors.email && <span className="error-message">{errors.email}</span>}
                </div>
                <div className={fieldClass('phone')}>
                  <label htmlFor="phone">{t('contact.phone')}</label>
                  <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} autoComplete="tel" inputMode="tel" placeholder="+32 491 33 37 77" aria-invalid={Boolean(errors.phone)} />
                  {errors.phone && <span className="error-message">{errors.phone}</span>}
                </div>
                <div className={`${fieldClass('subject')} contact-field-wide`}>
                  <label htmlFor="subject">{t('contact.subject')}</label>
                  <select id="subject" name="subject" value={formData.subject} onChange={handleChange} aria-invalid={Boolean(errors.subject)}>
                    <option value="">{t('contact.subjects.select')}</option>
                    <option value={t('contact.subjects.info')}>{t('contact.subjects.info')}</option>
                    <option value={t('contact.subjects.reservation')}>{t('contact.subjects.reservation')}</option>
                    <option value={t('contact.subjects.tarifs')}>{t('contact.subjects.tarifs')}</option>
                    <option value={t('contact.subjects.partnership')}>{t('contact.subjects.partnership')}</option>
                    <option value={t('contact.subjects.complaint')}>{t('contact.subjects.complaint')}</option>
                    <option value={t('contact.subjects.other')}>{t('contact.subjects.other')}</option>
                  </select>
                  {errors.subject && <span className="error-message">{errors.subject}</span>}
                </div>
                <div className={`${fieldClass('message')} contact-field-wide`}>
                  <label htmlFor="message">{t('contact.message')}</label>
                  <textarea id="message" name="message" value={formData.message} onChange={handleChange} rows="6" aria-invalid={Boolean(errors.message)} />
                  {errors.message && <span className="error-message">{errors.message}</span>}
                </div>
              </div>

              <button type="submit" className="submit-button" disabled={isSubmitting}>
                {isSubmitting ? t('contact.sending') : c.send}
                {!isSubmitting && <FontAwesomeIcon icon={faArrowRight} />}
              </button>
            </form>
          </motion.div>

          <motion.aside className="contact-details" {...motionProps} transition={{ duration: 0.5 }}>
            <span className="contact-details-eyebrow"><FontAwesomeIcon icon={faMessage} />{c.eyebrow}</span>
            <h2>{t('contact.info.title')}</h2>
            <p>{c.detailsIntro}</p>

            <div className="contact-detail-list">
              <a href="tel:+32491333777" className="contact-detail-item">
                <span><FontAwesomeIcon icon={faPhone} /></span>
                <small>{t('contact.info.phone.title')}</small>
                <strong>{t('contact.info.phone.content')}</strong>
              </a>
              <a href="mailto:info@maintendue.be" className="contact-detail-item">
                <span><FontAwesomeIcon icon={faEnvelope} /></span>
                <small>{t('contact.info.email.title')}</small>
                <strong>{t('contact.info.email.content')}</strong>
              </a>
              <div className="contact-detail-item">
                <span><FontAwesomeIcon icon={faLocationDot} /></span>
                <small>{t('contact.info.address.title')}</small>
                <strong>{t('contact.info.address.content')}</strong>
              </div>
              <div className="contact-detail-item">
                <span><FontAwesomeIcon icon={faClock} /></span>
                <small>{t('contact.info.hours.title')}</small>
                <strong>{t('contact.info.hours.content')}</strong>
              </div>
            </div>

            <div className="contact-response-note">
              <FontAwesomeIcon icon={faMessage} />
              <span><strong>{c.response}</strong><small>{c.responseText}</small></span>
            </div>
          </motion.aside>
        </div>
      </section>
    </main>
  );
}

export default Contact;
